/**
 * FindingAcceptedNextSteps — the landing spot after "Accept" on an agent
 * finding.
 *
 * Canon (docs/1-canon/22-ai-capability-taxonomy-and-registry.md §Detector,
 * docs/1-canon/27-decision-ontology.md §"Agents may propose... a human
 * records them"): a finding's lifecycle is accept/dismiss, and canon expects
 * an accepted finding to lead a human to record something — but Accept moved
 * the finding out of the active list with no navigation and no indication of
 * what to do next (a silent dead end). This renders inline, right where the
 * card used to be, for the one accepted finding until the reviewer dismisses
 * it or navigates away — the store already dropped the row from `active`.
 */

import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, X } from 'lucide-react';
import type { AgentFinding } from '../../../lib/agentFindingsService';
import { Button } from '../../ui/Button';

export interface FindingAcceptedNextStepsProps {
  finding: Pick<AgentFinding, 'id' | 'title' | 'itemIds'>;
  onDismiss: () => void;
}

export function FindingAcceptedNextSteps({ finding, onDismiss }: FindingAcceptedNextStepsProps) {
  const primaryItemId = finding.itemIds[0];
  const extraCount = finding.itemIds.length - 1;

  return (
    <div className="card border-[var(--green)] bg-[var(--green-dim)] space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          Accepted — {finding.title}
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} className="!p-1" title="Dismiss this notice">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {primaryItemId ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Link
            to={`/prioritization/items/${primaryItemId}`}
            className="inline-flex items-center gap-1 text-primary-700 hover:underline"
          >
            Open the item <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to={`/prioritization/decisions?attachItem=${primaryItemId}`}
            className="inline-flex items-center gap-1 text-primary-700 hover:underline"
          >
            Record a decision <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          {extraCount > 0 && (
            <span className="text-xs text-[var(--text-ter)]">+{extraCount} more item{extraCount === 1 ? '' : 's'} on this finding</span>
          )}
        </div>
      ) : (
        <p className="text-xs text-[var(--text-ter)]">This finding has no item to open.</p>
      )}
    </div>
  );
}

export default FindingAcceptedNextSteps;
